import Lenis from '@studio-freight/lenis'
import { Metrics } from '../core/Metrics';
import { isReducedMotion, isSmartphone, isTouch } from '../core/Basics';
import Scroll_Item from './Scroll_Item';
import { Functions } from '../utils/Functions';

export class Scroll {
  static isEnabled = false;
  static lenis = null;
  static rafId = null;

  static defaultOptions = {
    autoRaf: false,
    smooth: true,
    lerp: 0.1,
  };

  static observerOptions = {
    rootMargin: '0px 0px 0px 0px',
    threshold: 0,
  };

  static total_items = 0;
  static _items = [];
  static _itemsObserver = {};
  static _classItems = [];
  static _insidersItems = [];

  static set y(n) { this.lenis?.scrollTo(n); }
  static get y() { return this.lenis?.animatedScroll ?? 0; }
  static get limit() { return this.lenis?.limit; }
  static get progress() { return this.lenis?.progress; }
  static get direction() { return this.lenis?.direction; }
  static get actualScroll() { return this.lenis?.actualScroll; }
  static get speed() { return this.lenis?.velocity; }
  static get lastSpeed() { return this.lenis?.lastVelocity; }
  static get isScrolling() { return this.lenis?.isScrolling; }

  static init(options = {}, isStopped = false) {
    if (!this.lenis) {
      this.lenis = new Lenis({ ...this.defaultOptions, ...options });
      this.lenis.on('scroll', () => this.updateItems());
    }

    this.lenis.scrollTo(0, { immediate: true });
    if (isStopped) this.stop();

    this.setupObserver();

    setTimeout(() => {
      this._addAll();
      this.resize();
    }, 100);

    this.isEnabled = true;
  }

  static _registerClass(id, klass) {
    this._classItems.push({ id, class: klass });
  }

  static _getClass(item) {
    const idClass = item.getAttribute('data-class') || 'default';

    for (const entry of this._classItems) {
      if (entry.id === idClass) return entry.class;
    }

    if (item.getAttribute('data-class')) {
      console.warn(`scroll-item [${idClass}] no existe, posiblemente no hayas hecho el import`);
    }

    return Scroll_Item;
  }

  static _getClassVS(__item, __default) {
    let idClass = __item.getAttribute('data-class') || 'default';

    for (let i = 0, j = Scroll._classItems.length; i < j; i++) {
      if (idClass === Scroll._classItems[i].id) {
        return Scroll._classItems[i].class;
      }
    }

    if (__item.getAttribute('data-class')) {
      console.warn(`scroll-item [${__item.getAttribute('data-class')}] no existe, posiblemente no hayas hecho el import`);
    }

    return __default || VScroll_Item;
  }

  static _registerInsider(insider) {
    this._insidersItems.push({ id: insider.selector, class: insider });
  }

  static setupObserver() {
    if (this._observer) this._observer.disconnect();

    if (!isReducedMotion) {
      this._observer = new IntersectionObserver(
        entries => this.checkObserver(entries),
        this.observerOptions
      );
    }
  }

  static checkObserver(entries) {
    for (const entry of entries) {
      const id = Functions.getId(entry.target);
      const item = this._itemsObserver[id];
      entry.isIntersecting ? item.show() : item.hide();
    }
  }

  static start() {
    this.lenis?.start();
  }

  static stop() {
    this.lenis?.stop();
  }

  static loop(time) {
    if (this.lenis) {
      this.lenis.raf(time * 1000);
    }
  }

  static updateItems() {
    for (let i = 0; i < this.total_items; i++) {
      this._items[i].update();
    }
  }

  static resize() {
    this._items.forEach(item => {
      item.resize();
      item.resizeLimits(Metrics.WIDTH, Metrics.HEIGHT);
    });
  }

  static show(){}
  static hide(){}

  // Scroll navigation
  static goto(n) {
    this.lenis.scrollTo(n);
  }

  static directGoto(n) {
    this.lenis.scrollTo(n, { offset: -Metrics.OFFSET, immediate: true });
  }

  static gotoDOMElement(el) {
    this.lenis.scrollTo(el, { offset: -Metrics.OFFSET });
  }

  static gotoID(id) {
    this.lenis.scrollTo(`#${id}`, { offset: -Metrics.OFFSET });
  }

  static gotoAnchor(anchor) {
    this.gotoID(anchor.startsWith('#') ? anchor.substring(1) : anchor);
  }

  static gotoPercentage(n) {
    this.lenis.scrollTo(this.lenis.limit * n, { offset: -Metrics.OFFSET });
  }

  static _addAll(selector = "[scroll-item]") {
    const items = document.querySelectorAll(selector);

    for (const item of items) {
      item.setAttribute("scroll-item-loaded", "");
      item.removeAttribute("scroll-item");

      const allowMobile = isTouch ? item.getAttribute("data-avoid-mobile") === null : true;
      const allowSmartphone = isSmartphone ? item.getAttribute("data-avoid-smartphone") === null : true;

      if (allowMobile && allowSmartphone) {
        const Class = this._classItems.length > 0 ? this._getClass(item) : Scroll_Item;
        const instance = new Class(item, this.total_items, this);
        this.total_items = this._items.push(instance);
        this._itemsObserver[instance.id] = instance;

        if (!isReducedMotion) {
          this._observer.observe(instance.item);
        }
      }
    }
  }

  static dispose() {
    this.lenis?.destroy();
    this.lenis = null;

    this._observer?.disconnect();
    this._observer = null;

    for (const item of this._items) {
      item.dispose();
    }

    this._items = [];
    this.total_items = 0;
    this._itemsObserver = {};
  }
}
